/**
 * Instant Recruitment Service
 *
 * Handles recruitment resolution at submission time rather than tick end.
 * Player selects a candidate from generated options, and the candidate
 * is immediately created in the database with AP deducted.
 *
 * Flow:
 * 1. Player selects source → 3 candidates generated (client-side)
 * 2. Player picks one → executeRecruitmentInstantly called
 * 3. Candidate created in DB, AP deducted, action recorded as 'resolved'
 */

import { supabase } from '@/lib/supabase'
import { RECRUITMENT_SOURCES } from '@/constants/recruitment'
import type { Candidate, RecruitmentSource } from '@/types'
import type { RecruitCandidateData } from '@/lib/resolvers/recruitResolver'

// ============================================================================
// Types
// ============================================================================

export interface ExecuteRecruitmentInput {
  partyId: string
  nationId: string
  tick: number
  source: RecruitmentSource
  candidateData: RecruitCandidateData
  /** For poaching: the party being poached from */
  targetPartyId?: string
  /** For poaching: the original candidate being taken */
  targetCandidateId?: string
}

export interface ExecuteRecruitmentResult {
  success: boolean
  candidate?: Candidate
  actionId: string
  failureReason?: string
  summary: string
}

// ============================================================================
// Tuning Constants
// ============================================================================

const POACHED_LOYALTY_PENALTY = 30
const POACHED_MIN_LOYALTY = 20

// ============================================================================
// Main Function
// ============================================================================

/**
 * Execute recruitment instantly, creating the candidate and recording the action.
 *
 * For standard recruitment: always succeeds (candidate is created immediately).
 * For poaching: may fail if target candidate is no longer available.
 * AP is always deducted (failed poach attempts still cost resources).
 */
export async function executeRecruitmentInstantly(
  input: ExecuteRecruitmentInput
): Promise<ExecuteRecruitmentResult> {
  const { partyId, tick, source, candidateData, targetPartyId, targetCandidateId } = input

  const apCost = RECRUITMENT_SOURCES[source].apCost

  // 1. For poaching, verify target candidate still available
  if (source === 'poached' && targetCandidateId) {
    const { data: targetCandidate } = await supabase
      .from('candidates')
      .select('*')
      .eq('id', targetCandidateId)
      .eq('is_active', true)
      .single()

    if (!targetCandidate) {
      // Create failed action record (still costs AP)
      const { data: action } = await supabase
        .from('party_actions')
        .insert({
          party_id: partyId,
          tick,
          action_type: 'recruit',
          action_subtype: source,
          target_type: 'candidate',
          target_id: targetCandidateId,
          action_points_spent: apCost,
          parameters: { source, candidate_data: candidateData, target_party_id: targetPartyId },
          status: 'resolved',
          results: { success: false, reason: 'Target candidate no longer available' },
        })
        .select()
        .single()

      await deductActionPoints(partyId, apCost)

      return {
        success: false,
        actionId: action?.id ?? '',
        failureReason:
          'Target candidate is no longer available. They may have left politics or been recruited by another party.',
        summary: 'Poaching attempt failed: target no longer available.',
      }
    }

    // Apply loyalty penalty for poached candidates
    candidateData.loyalty = Math.max(POACHED_MIN_LOYALTY, candidateData.loyalty - POACHED_LOYALTY_PENALTY)

    // Inherit recognition from target
    candidateData.recognition = targetCandidate.recognition ?? 30

    // Mark original candidate as inactive (they defected)
    await supabase.from('candidates').update({ is_active: false }).eq('id', targetCandidateId)
  }

  // 2. Create the candidate
  const { data: candidate, error: candidateError } = await supabase
    .from('candidates')
    .insert({
      party_id: partyId,
      name: candidateData.name,
      age: candidateData.age,
      ideology_vector: candidateData.ideology_vector,
      loyalty: candidateData.loyalty,
      recognition: source === 'poached' ? candidateData.recognition : candidateData.recognition,
      charisma: candidateData.charisma,
      admin_skill: candidateData.admin_skill,
      debate_skill: candidateData.debate_skill,
      scandal_prone: candidateData.scandal_prone,
      ambition: candidateData.ambition,
      traits: [],
      role: 'none',
      assigned_district_id: null,
      recruited_tick: tick,
      recruitment_source: source,
      is_active: true,
    })
    .select()
    .single()

  if (candidateError) throw candidateError

  // 3. Create action record with status 'resolved'
  const { data: action, error: actionError } = await supabase
    .from('party_actions')
    .insert({
      party_id: partyId,
      tick,
      action_type: 'recruit',
      action_subtype: source,
      target_type: source === 'poached' ? 'candidate' : 'national',
      target_id: source === 'poached' ? targetCandidateId : null,
      action_points_spent: apCost,
      parameters: {
        source,
        candidate_data: candidateData,
        ...(source === 'poached' ? { target_party_id: targetPartyId } : {}),
      },
      status: 'resolved',
      results: {
        success: true,
        candidate_id: candidate.id,
        candidate_name: candidate.name,
        summary: generateRecruitmentSummary(source, candidate),
      },
    })
    .select()
    .single()

  if (actionError) throw actionError

  // 4. Deduct AP from party
  await deductActionPoints(partyId, apCost)

  return {
    success: true,
    candidate: candidate as Candidate,
    actionId: action.id,
    summary: generateRecruitmentSummary(source, candidate as Candidate),
  }
}

// ============================================================================
// Helpers
// ============================================================================

function generateRecruitmentSummary(source: RecruitmentSource, candidate: { name: string; loyalty: number }): string {
  const sourceLabel = RECRUITMENT_SOURCES[source].label
  if (source === 'poached') {
    return `Successfully recruited ${candidate.name} from a rival party. They join with ${candidate.loyalty}% loyalty due to switching allegiances.`
  }
  return `${candidate.name} has joined the party from ${sourceLabel}. Starting loyalty: ${candidate.loyalty}%.`
}

async function deductActionPoints(partyId: string, amount: number): Promise<void> {
  const { data: party } = await supabase
    .from('parties')
    .select('action_points')
    .eq('id', partyId)
    .single()

  await supabase
    .from('parties')
    .update({ action_points: Math.max(0, (party?.action_points ?? 0) - amount) })
    .eq('id', partyId)
}
